<template>
  <div>
    <div class="schoolNameArea" :class="{ isPc: !isSp }">
      <InputLabel
        class="labelArea"
        :class="{ isPc: !isSp, isHasLabel: !isSp && (required || optional), isPcModal: isPcModal }"
        :required="required"
        :optional="optional"
        :is-pc-modal="isPcModal"
      >
        学校名
      </InputLabel>
      <InputItem
        v-model="handleInputValue"
        input-type="text"
        placeholder-value="高校名を入力すると選択肢が表示されます"
        class="inputItem"
        :class="{ hasError: hasError, isPc: !isSp, isPcModal: isPcModal }"
        :disabled="disabled"
        :is-pc-modal="isPcModal"
        input-mode
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
      />
    </div>
    <div class="underInputArea" :class="{ isPc: !isSp, isPcModal: isPcModal }">
      <SuggestList
        v-show="isDisplaySuggest && arrangedSuggestList.length > 1"
        class="suggestArea"
        :class="{ isPc: !isSp }"
        :list="arrangedSuggestList"
        @onClick="onClick"
      />
      <PartialErrorMessage :is-sp-flg="true" :error="displayError" class="partialErrorComponent" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { throttle } from 'throttle-debounce';
import InputLabel from '../Text/InputLabel.vue';
import InputItem from './InputItem.vue';
import PartialErrorMessage from '../PartialErrorMessage.vue';
import { GENERAL_VALIDATION_MESSAGE, SPECIFY_VALIDATION_MESSAGE } from '../../../../helper/_validationCheckMessages';
import SuggestList from './Suggest/SuggestList.vue';

export default {
  name: 'SchoolName',
  components: {
    InputLabel,
    InputItem,
    PartialErrorMessage,
    SuggestList,
  },
  model: {
    prop: 'inputValue',
    event: 'change',
  },
  props: {
    inputValue: {
      type: String,
      required: true,
    },
    /**
     * 外部からのエラー
     * このコンポーネントに定義しないような独自のバリデーションを持つ場合、
     * 親コンポーネントでv-modelの値をwatchとかしてバリデーションチェックを行い、
     * errorFromOuterにpropsで渡すと、最後の項目としてチェック入る。
     * 空文字・undefinedを渡すことで『エラー無し』扱いになる。
     */
    errorFromOuter: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    schoolNameValidCheckFlg: {
      type: Boolean,
      required: false,
      default: false,
    },
    prefCode: {
      type: String,
      required: false,
      default: '',
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    selectedSchoolCode: {
      type: String,
      required: true,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    /**
     * 資料請求確認画面PC、 予約確認画面PCの時のみtrue
     * 両画面の変更フォームをモーダル内に表示する際にスタイル調整するため。
     */
    isPcModal: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    // 既に学校名がある場合はtrue
    // trueの場合はフォーカスしてもサジェスト発火＆入力チェックしない
    inputFlg: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 既に登録している学校名を保持する
    confirmedSchoolValue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDisplaySuggest: false,
      validationError: undefined,
      suggestList: {
        schoolList: [],
        prefectureList: [],
      },
      // 入力確定フラグ
      isConfirmed: this.inputFlg,
      // 【IE対応】IEでのみ、propsの更新タイミングが『すべて終わった後』でリアクティブにならないので、input内容を内部で複製する
      onInputValue: this.inputValue,
      // 【IE対応】IEでのみ、inputがページ開いた時に発火してしまうのを防ぐためのもの
      focusFlg: false,
      // 【IE対応】IEでのみ、propsの更新タイミングが『すべて終わった後』でリアクティブにならないので、クリック内容を内部で複製する
      onClickedInput: this.inputValue, // IE不要になったらinputValueに統一してください
      onClickedSchoolCode: this.selectedSchoolCode, // IE不要になったらselectedSchoolCodeに統一してください
      onClickedConfirmValue: this.confirmedSchoolValue, // IE不要になったらすべてconfirmedSchoolValueに統一してください
      onClickFlg: false,
      // 【IE対応】IEにおいてIME未確定のままクリックした時の下記事象への対処のためにクリック後はset/inputイベントをスキップする
      // - クリックデータ反映した後にIME確定イベントが走ってクリックデータ上書きしてしまう
      //   - このとき、親のデータ（入力欄に表示されるデータ）は反映されているように見えるが、コンポーネント内のpropsは更新されていないので利用できない
      //   - なので、『このコンポーネント内の入力データ（onInputValue）』クリック後のIME確定の影響を受けないように、クリック後更新をすべてスキップする
      // 親の更新はonClick内ですべてemit済
      isInClickProcess: false,
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    arrangedSuggestList() {
      const joinedList = [
        ...this.suggestList.schoolList,
        {
          name: '一覧にない場合はこちら',
          value: undefined,
          background: 'grey',
        },
        ...this.suggestList.prefectureList,
      ];
      return joinedList.map((item, index) => ({ id: index, ...item }));
    },
    displayError() {
      if (this.validationError) return this.validationError;
      return this.errorFromOuter;
    },
    hasError() {
      // displayError自体がundefinedならエラーない
      if (!this.displayError) return false;
      // displayError自体がlengthもってるならエラーあり
      if (this.displayError.length) return true;
      return false;
    },
    handleInputValue: {
      get() {
        return this.inputValue;
      },
      set(value) {
        // 【IEのみ】クリックイベントが確定イベントよりも先に走ってしまい、クリックした結果を確定した結果で上書きしてしまうので、クリック時のみ確定を1回スキップする
        // バリデーションチェックはonClickedInputに寄せる等しており、上の値は空欄チェックくらいでしかみてないので、これで問題ないはずだが、
        // IE以外においては無駄処理でしかないので、IE対応不要になったら除去をお願いします
        if (this.onClickFlg) {
          this.onClickFlg = false;
          return;
        }
        if (this.isInClickProcess) return;
        this.$emit('change', value);
      },
    },
  },
  watch: {
    schoolNameValidCheckFlg() {
      // 属性変更のタイミングなどでバリデチェックを強制発火する
      this.validCheck();
    },
  },
  methods: {
    getSuggestList: throttle(500, function getSuggestList(value) {
      const param = this.prefCode
        ? {
            input: value,
            todofukenCd: this.prefCode,
            limit: 200,
          }
        : {
            input: value,
            limit: 200,
          };

      axios
        .post('/net2/profileItem/kokoSearchJson', qs.stringify(param))
        .then((response) => {
          if (!response) throw Error;
          if (!response.data) throw Error;

          this.suggestList.schoolList = response.data.kokoList.map((koko) => ({
            value: koko.kokoCd,
            name: koko.kokoName,
          }));

          this.suggestList.prefectureList = response.data.sonotaList.map((sonota) => ({
            value: sonota.kokoCd,
            name: sonota.kokoName,
          }));
        })
        .catch(() => {
          this.suggestList.schoolList = [];
          this.suggestList.prefectureList = [];
        });
    }),
    onInput(event) {
      if (this.isInClickProcess) return;
      this.onInputValue = event.target.value;
      if (this.focusFlg === false) return;
      // 元々入ってた値とinput後の値が同じであれば、確定済みにする&エラー文言を削除&サジェスト出さない
      if (event.target.value === this.confirmedSchoolValue.name) {
        this.isConfirmed = true;
        this.validationError = undefined;
        this.$emit('watchValidation', !!this.validationError);
        return;
      }
      // inputが1文字でも発生したらconfirmフラグ倒す
      this.isConfirmed = false;
      // setは日本語確定まで動作しないので、inputでサジェスト発火
      this.displaySuggest(event.target.value);
    },
    onClick(value) {
      if (this.isSp) {
        const schoolNameInputArea = this.$el.querySelector('.schoolNameArea');
        const inputItem = schoolNameInputArea.lastElementChild;
        inputItem.value = value.name;
      }
      this.isInClickProcess = true;
      // サジェストクリックで確定フラグ立てる
      this.isConfirmed = true;
      this.isDisplaySuggest = false;
      // IE対応: このへんのonClick系はIE不要になれば決して問題ないはずなので、確認の上除去してください
      this.onClickedInput = value.name;
      this.onClickedSchoolCode = value.value;
      this.onClickedConfirmValue = value;
      this.onClickFlg = true;
      // IE対応: 入力とクリック内容の同期: IE対応終わればthis.handleInputValueだけで済むはずなので確認の上除去してください
      this.onInputValue = value.name;
      this.$emit('setConfirmValue', value);
      this.$emit('change', value.name);
      this.$emit('onClick', value.value);
    },
    generateValidationCheckMessage() {
      this.isInClickProcess = false;

      // requiredだけど未入力: 未入力エラー
      if (this.required && this.handleInputValue === '') {
        return `学校名${GENERAL_VALIDATION_MESSAGE.EMPTY}`;
      }
      // requiredじゃない: エラー無し
      if (!this.required && this.handleInputValue === '') {
        return undefined;
      }
      // 入力欄に入力してある値がリストにない: リスト内から選択してないエラー
      const emitValue = this.arrangedSuggestList.filter((item) => item.name === this.onInputValue).length;
      if (emitValue === 0) {
        return SPECIFY_VALIDATION_MESSAGE.SCHOOL_NAME.VALIDATION_ERROR;
      }
      // 学校コードが選択されてない: リスト内から選択してないエラー
      if (this.selectedSchoolCode === '' && this.onClickedSchoolCode === '') {
        return SPECIFY_VALIDATION_MESSAGE.SCHOOL_NAME.VALIDATION_ERROR;
      }
      return undefined;
    },
    validCheck() {
      this.validationError = this.generateValidationCheckMessage();
      this.$emit('watchValidation', !!this.validationError);
    },
    async displaySuggest(value) {
      // 空文字でもvalueを利用したいのでstringチェックする
      const currentValue = typeof value === 'string' ? value : this.handleInputValue;
      if (currentValue.length > 0) {
        this.getSuggestList(currentValue);
        this.isDisplaySuggest = true;
      } else {
        this.isDisplaySuggest = false;
      }
    },
    onFocus() {
      this.$emit('focus');
      this.focusFlg = true;
      // 確定済みならfocusでサジェスト発火しない
      if (this.isConfirmed) return;
      this.displaySuggest();
    },
    onBlur() {
      this.$emit('blur');
      this.focusFlg = false;

      // 入力した学校名と確定した学校名が違ったら、サジェストリストから選んでないため、学校コードを空にしてエラーを起こさせる
      if (this.onClickedInput !== this.onClickedConfirmValue.name) {
        this.$emit('handleSchoolCd', '');
        this.isConfirmed = false;
      } else {
        // 同じ場合は、学校コードが空になっている場合があるので、再度代入する
        this.$emit('handleSchoolCd', this.onClickedConfirmValue.value);
      }

      // blurしたらサジェストを非表示
      this.isDisplaySuggest = false;

      // 確定済み かつ 入力エラーが無いならblurで再チェックしない
      if (this.isConfirmed && !this.validationError) return;
      this.validCheck();
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

$input-item-width: 432px;

.schoolNameArea {
  .labelArea.isPc {
    width: 120px;
    margin: auto 0;

    &.isHasLabel {
      width: 184px;
    }

    &.isPcModal {
      width: 116px;
    }
  }

  .inputItem.isPc {
    width: $input-item-width;

    &.isPcModal {
      width: 396px;
      margin-left: 32px;
    }
  }

  &.isPc {
    display: flex;
  }
}

.underInputArea.isPc {
  margin-left: 184px;

  &.isPcModal {
    margin-left: 148px;
  }
}

.suggestArea.isPc {
  width: $input-item-width;
}

.hasError,
.hasError:focus {
  border: solid 1px #e62525;
}

.partialErrorComponent {
  margin: 8px 0;
}
</style>
